<script setup lang="ts">
import { computed } from "vue";

import type { VoiceNote } from "@/types/album";

import { formatClock, formatDuration } from "@/lib/format";
import { MOMENT_LABELS } from "@/types/album";

/**
 * Un message du livre d'or vocal, présenté comme un polaroïd : sans image à
 * montrer, la carte affiche une onde et une icône de micro. La galerie garde
 * ainsi une seule grammaire visuelle — tout ce que les invités déposent est un
 * polaroïd, qu'il porte une photo, une vidéo ou une voix.
 */

const props = defineProps<{ note: VoiceNote }>();
const emit = defineEmits<{ open: [id: string] }>();

const durationLabel = computed(() => formatDuration(props.note.durationMs));
const timeLabel = computed(() => formatClock(props.note.createdAt));
const momentLabel = computed(() => (props.note.moment ? MOMENT_LABELS[props.note.moment] : null));

const ariaLabel = computed(
  () =>
    `Message vocal de ${props.note.author}${momentLabel.value ? ` — ${momentLabel.value}` : ""}, ${durationLabel.value}`,
);

/**
 * Barres d'onde purement décoratives. Leurs hauteurs sont dérivées de
 * l'identifiant de la note : chaque message garde ainsi la même silhouette
 * d'un rendu à l'autre, sans dépendre d'un tirage aléatoire.
 */
const bars = computed(() => {
  const seed = [...props.note.id].reduce((total, char) => total + char.charCodeAt(0), 0);
  return Array.from({ length: 22 }, (_, index) => {
    const wave = Math.sin((seed + index * 37) * 0.7);
    return 24 + Math.abs(wave) * 62;
  });
});
</script>

<template>
  <button
    type="button"
    class="media-card voice-card"
    data-testid="voice-card"
    :data-note-id="note.id"
    :aria-label="ariaLabel"
    @click="emit('open', note.id)"
  >
    <span class="voice-card__stage">
      <span class="voice-card__wave" aria-hidden="true">
        <i v-for="(height, index) in bars" :key="index" :style="{ height: `${height}%` }"></i>
      </span>
      <span class="voice-card__badge" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <rect x="9" y="3" width="6" height="12" rx="3"></rect>
          <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M9 21h6"></path>
        </svg>
      </span>
      <span v-if="momentLabel" class="voice-card__moment">{{ momentLabel }}</span>
      <span class="voice-card__duration">{{ durationLabel }}</span>
    </span>

    <span class="media-card__caption">Un mot pour les mariés</span>

    <span class="media-card__overlay">
      <span
        ><strong>{{ note.author }}</strong
        ><small>{{ timeLabel }}</small></span
      >
      <i aria-hidden="true">♪</i>
    </span>
  </button>
</template>

<style scoped>
/* Le cadre du polaroïd est repris tel quel de `MediaCard` : les deux cartes
   doivent être indiscernables au premier coup d'œil dans la mosaïque. */
.media-card {
  position: relative;
  display: inline-flex;
  align-items: stretch;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  margin: 0 0 18px;
  padding: 7px 7px 13px;
  color: var(--color-ink);
  border: 0;
  border-radius: 1px;
  text-align: left;
  background: var(--color-ivory);
  box-shadow: var(--shadow-polaroid);
  break-inside: avoid;
  overflow: hidden;
  transition:
    transform 0.32s ease,
    box-shadow 0.32s ease;
  animation: var(--animate-card-in);
}

.media-card:hover,
.media-card:focus-visible {
  z-index: 3;
  outline: none;
  box-shadow: var(--shadow-polaroid-lift);
}

.voice-card__stage {
  position: relative;
  display: grid;
  order: 1;
  height: 172px;
  place-items: center;
  background: linear-gradient(160deg, #6f8069, #3e5247 62%, #2f3f36);
}

.voice-card__wave {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 0 14px;
  opacity: 0.4;
}

.voice-card__wave i {
  display: block;
  width: 3px;
  border-radius: 99px;
  background: var(--color-ivory);
}

.voice-card__badge {
  position: relative;
  display: grid;
  width: 52px;
  height: 52px;
  place-items: center;
  color: white;
  border: 1px solid rgb(255 255 255 / 0.42);
  border-radius: 50%;
  background: rgb(25 31 27 / 0.42);
  backdrop-filter: blur(7px);
}

.voice-card__badge svg {
  width: 22px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.5;
}

.voice-card__moment {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 8px;
  color: white;
  border: 1px solid rgb(255 255 255 / 0.28);
  background: rgb(20 25 22 / 0.4);
  border-radius: 99px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.voice-card__duration {
  position: absolute;
  bottom: 12px;
  left: 12px;
  padding: 4px 7px;
  color: white;
  border-radius: 99px;
  background: rgb(20 25 22 / 0.57);
  font-size: 11px;
}

.media-card__caption {
  order: 2;
  padding: 11px 5px 3px;
  color: var(--color-olive);
  font-family: var(--font-script);
  font-size: clamp(21px, 5.6vw, 29px);
  line-height: 1;
}

.media-card__overlay {
  display: flex;
  order: 3;
  justify-content: space-between;
  align-items: center;
  padding: 4px 5px 2px;
  color: var(--color-muted);
}

.media-card__overlay strong {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.media-card__overlay strong::before {
  content: "Enregistré par ";
}

.media-card__overlay small {
  margin-left: 5px;
  font-size: 10px;
  letter-spacing: 0.03em;
}

.media-card__overlay small::before {
  content: "· ";
}

.media-card__overlay > i {
  color: var(--color-rose);
  font-size: 15px;
  font-style: normal;
}

@media (min-width: 760px) {
  .media-card {
    margin-bottom: 29px;
    padding: 10px 10px 16px;
  }

  .voice-card__stage {
    height: 220px;
  }

  .media-card__caption {
    font-size: 28px;
  }
}
</style>
